import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../services/api';
import { SessionStatus, SelectedUnit, ActiveSessionResponse } from '../types/auth';
import type { AppDispatch } from './index';

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionStatus: SessionStatus | null;
  selectedUnit: SelectedUnit | null;
  userName: string | null;
  permissions: string[];
  initialLoadTotal: number;
  initialLoadCompleted: number;
}

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  sessionStatus: null,
  selectedUnit: null,
  userName: null,
  permissions: [],
  initialLoadTotal: 0,
  initialLoadCompleted: 0,
};

// Module-level promise cache — mirrors the useRef approach in the old AuthContext
let pendingCheckAuth: Promise<void> | null = null;

// Returns true if the non-HttpOnly indicator cookie is present and set to "true"
function hasSessionCookie(): boolean {
  return document.cookie.split(';').some((c) => c.trim() === 'has_session=true');
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthenticated(state, action: PayloadAction<boolean>) {
      state.isAuthenticated = action.payload;
      if (action.payload) {
        pendingCheckAuth = null;
      }
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setSessionData(state, action: PayloadAction<ActiveSessionResponse>) {
      state.isAuthenticated = true;
      state.sessionStatus = action.payload.status;
      state.selectedUnit = action.payload.selected_unit ?? null;
      state.userName = action.payload.username;
      state.permissions = action.payload.permissions ?? [];
      pendingCheckAuth = null;
    },
    setInitialLoadTotal(state, action: PayloadAction<number>) {
      state.initialLoadTotal = action.payload;
      state.initialLoadCompleted = 0;
    },
    setInitialLoadProgress(state, action: PayloadAction<number>) {
      state.initialLoadCompleted = action.payload;
    },
    // Updates total without resetting initialLoadCompleted — used when total grows
    // dynamically mid-load (e.g. employee sync discovers how many chunks it needs).
    setInitialLoadTotalOnly(state, action: PayloadAction<number>) {
      state.initialLoadTotal = action.payload;
    },
    resetAuth(state) {
      state.isAuthenticated = false;
      state.isLoading = false;
      state.sessionStatus = null;
      state.selectedUnit = null;
      state.userName = null;
      state.permissions = [];
      state.initialLoadTotal = 0;
      state.initialLoadCompleted = 0;
      pendingCheckAuth = null;
    },
  },
});

export const { setAuthenticated, setLoading, setSessionData, resetAuth, setInitialLoadTotal, setInitialLoadProgress, setInitialLoadTotalOnly } = authSlice.actions;

// Plain thunk — cookie-first check, then backend session validation
export const checkAuth =
  (force: boolean = false) =>
  (dispatch: AppDispatch): Promise<void> => {
    if (force) {
      pendingCheckAuth = null;
    }

    if (pendingCheckAuth) {
      return pendingCheckAuth;
    }

    pendingCheckAuth = (async () => {
      dispatch(setLoading(true));
      try {
        // Step 1: Client-side cookie check — if no indicator cookie, skip the API call
        if (!hasSessionCookie()) {
          dispatch(setAuthenticated(false));
          return;
        }
        // Step 2: Backend session validation — returns user/session info on success
        const session = await apiService.getActiveSession();
        dispatch(setSessionData(session));
      } catch (error: any) {
        if (error?.status !== 401 && error?.message && !error.message.includes('401')) {
          console.error('Auth check error:', error);
        }
        dispatch(setAuthenticated(false));
      } finally {
        dispatch(setLoading(false));
      }
    })();

    return pendingCheckAuth;
  };

export const logoutThunk = () => async (dispatch: AppDispatch): Promise<void> => {
  try {
    await apiService.logout();
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    dispatch(resetAuth());
  }
};

export default authSlice.reducer;
